/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const SaveElectronicPrescriptionPopup = ({
  selectedPrescriptionId,
  setSelectedPrescriptionId,
  afterActionClose,
  setShowAlert,
  setAlertMessage,
  setMessageType,
  addOrUpdatePrescription,
  selectedPrescription,
}) => {
  const { t } = useTranslation();

  // Form state
  const [patientName, setPatientName] = useState("");
  const [medication, setMedication] = useState("");
  const [dosage, setDosage] = useState("");
  const [instructions, setInstructions] = useState("");
  const [prescriptionDate, setPrescriptionDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (selectedPrescription) {
      // Load data for editing
      setPatientName(selectedPrescription.patientName || "");
      setMedication(selectedPrescription.medication || "");
      setDosage(selectedPrescription.dosage || "");
      setInstructions(selectedPrescription.instructions || "");
      setPrescriptionDate(
        selectedPrescription.prescriptionDate ||
          new Date().toISOString().split("T")[0]
      );
    } else {
      resetForm();
    }
  }, [selectedPrescription]);

  const resetForm = () => {
    setPatientName("");
    setMedication("");
    setDosage("");
    setInstructions("");
    setPrescriptionDate(new Date().toISOString().split("T")[0]);
    setErrors({});
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    if (!patientName.trim())
      newErrors.patientName = "Patient Name is required.";
    if (!medication.trim()) newErrors.medication = "Medication is required.";
    if (!dosage.trim()) newErrors.dosage = "Dosage is required.";
    if (!prescriptionDate) newErrors.prescriptionDate = "Date is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save prescription
  const savePrescription = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    // Simulate API call
    setTimeout(() => {
      const prescriptionData = {
        patientName,
        medication,
        dosage,
        instructions,
        prescriptionDate,
        status: "Active",
      };
      addOrUpdatePrescription(prescriptionData);

      setIsSaving(false);
      setShowAlert(true);
      setAlertMessage(
        selectedPrescriptionId
          ? "Electronic prescription updated successfully!"
          : "Electronic prescription added successfully!"
      );
      setMessageType("success");
      resetForm();
      setSelectedPrescriptionId(null);
      afterActionClose();
    }, 1000);
  };

  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="saveElectronicPrescriptionOffCanvas"
      aria-labelledby="saveElectronicPrescriptionOffCanvasLabel"
      style={{ width: "600px" }}
    >
      <div className="offcanvas-header bg-light">
        <h5
          className="offcanvas-title fw-bold"
          id="saveElectronicPrescriptionOffCanvasLabel"
        >
          {selectedPrescriptionId
            ? t("Edit Electronic Prescription")
            : t("Add New Electronic Prescription")}
        </h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={() => {
            resetForm();
            setSelectedPrescriptionId(null);
          }}
        ></button>
      </div>
      <div className="offcanvas-body">
        <form>
          {/* Patient Name */}
          <div className="form-group mb-4">
            <label
              htmlFor="patientName"
              className="d-block fs-sm fw-semibold mb-2 text-dark"
            >
              Patient Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${
                errors.patientName ? "is-invalid" : ""
              }`}
              id="patientName"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              placeholder="Enter patient name"
            />
            {errors.patientName && (
              <div className="invalid-feedback">{errors.patientName}</div>
            )}
          </div>

          {/* Medication */}
          <div className="form-group mb-4">
            <label
              htmlFor="medication"
              className="d-block fs-sm fw-semibold mb-2 text-dark"
            >
              Medication <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${
                errors.medication ? "is-invalid" : ""
              }`}
              id="medication"
              value={medication}
              onChange={(e) => setMedication(e.target.value)}
              placeholder="Enter medication name"
            />
            {errors.medication && (
              <div className="invalid-feedback">{errors.medication}</div>
            )}
          </div>

          {/* Dosage */}
          <div className="form-group mb-4">
            <label
              htmlFor="dosage"
              className="d-block fs-sm fw-semibold mb-2 text-dark"
            >
              Dosage <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.dosage ? "is-invalid" : ""}`}
              id="dosage"
              value={dosage}
              onChange={(e) => setDosage(e.target.value)}
              placeholder="Enter dosage"
            />
            {errors.dosage && (
              <div className="invalid-feedback">{errors.dosage}</div>
            )}
          </div>

          {/* Instructions */}
          <div className="form-group mb-4">
            <label
              htmlFor="instructions"
              className="d-block fs-sm fw-semibold mb-2 text-dark"
            >
              Instructions
            </label>
            <textarea
              className="form-control"
              id="instructions"
              rows="3"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Enter instructions"
            ></textarea>
          </div>

          {/* Prescription Date */}
          <div className="form-group mb-4">
            <label
              htmlFor="prescriptionDate"
              className="d-block fs-sm fw-semibold mb-2 text-dark"
            >
              Prescription Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className={`form-control ${
                errors.prescriptionDate ? "is-invalid" : ""
              }`}
              id="prescriptionDate"
              value={prescriptionDate}
              onChange={(e) => setPrescriptionDate(e.target.value)}
            />
            {errors.prescriptionDate && (
              <div className="invalid-feedback">{errors.prescriptionDate}</div>
            )}
          </div>
        </form>
      </div>
      <div className="offcanvas-footer bg-light p-3">
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            data-bs-dismiss="offcanvas"
            onClick={() => {
              resetForm();
              setSelectedPrescriptionId(null);
            }}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={savePrescription}
            disabled={isSaving}
          >
            {isSaving
              ? t("Saving...")
              : selectedPrescriptionId
              ? t("Update")
              : t("Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveElectronicPrescriptionPopup;
